import * as _ from "underscore";

import * as dateUtils from "./dateUtils.js";

let _wordsPerDay = 25;

export const DAY = 24 * 3600 * 1000;
export const INTERVAL_1 = 1 * DAY;
export const INTERVAL_2 = 6 * DAY;
export const GETTING_THERE_INTERVAL = INTERVAL_1 + DAY;
export const MASTERED_INTERVAL = INTERVAL_2 + DAY;
export const NEW_WORD_AVERAGE = DAY / INTERVAL_1;

/**
 * Which exercise produced a recall, stored on each Recall document so recall
 * history can be analysed per exercise type. "remembered" is the reader
 * popup's "I remembered" button; "lookup" is looking a saved word up while
 * reading, which counts as a failed recall.
 * @typedef {"classic" | "blitz" | "mastery" | "chat" | "lookup" | "remembered"} RecallSource
 */

/**
 * Difficulty of recognising a word while reading: seeing the foreign word in
 * context is a passive recall. "I remembered" is its success and a lookup its
 * failure.
 */
export const READING_DIFFICULTY = 0.4;

/** Set to true to show the changes to each word's SRS fields in the console after each recall */
const DEBUG_SRS = false;

/**
 * @typedef {Object} ReviewPaceSettings
 * @property {number} firstInterval - Interval after a perfect (ease 5, active)
 *   recall of a word with no interval yet. A "good" (ease < 5) recall earns
 *   half this, and passive recalls halve it again — matching how the default
 *   schedule has always behaved.
 * @property {number} intervalMultiplier - Multiplier on the additional interval
 *   growth earned by a successful recall, after discounting recent exposure.
 *   Does not multiply the existing interval itself.
 */

/**
 * Scheduling presets for the user's `reviewPace` preference. Wider paces wait
 * longer before the first repeat of a word and grow intervals faster (so users
 * see fewer repeats and more new words); closer paces bring repeats back
 * sooner.
 *
 * Uses a null-prototype object so lookups can never accidentally resolve to
 * inherited `Object.prototype` members like `toString` or `constructor`.
 *
 * @type {Record<"closer" | "standard" | "wider" | "widest", ReviewPaceSettings>}
 */
export const REVIEW_PACE_SETTINGS = Object.freeze(
  Object.assign(Object.create(null), {
    closer: Object.freeze({ firstInterval: DAY / 2, intervalMultiplier: 0.65 }),
    standard: Object.freeze({ firstInterval: DAY, intervalMultiplier: 1 }),
    wider: Object.freeze({ firstInterval: 2 * DAY, intervalMultiplier: 1.7 }),
    widest: Object.freeze({ firstInterval: 3 * DAY, intervalMultiplier: 2.4 }),
  })
);

/**
 * Whitelist of valid `reviewPace` values, derived from
 * `REVIEW_PACE_SETTINGS`. Use this when validating user input before
 * persisting a `reviewPace` value.
 *
 * @type {readonly string[]}
 */
export const REVIEW_PACES = Object.freeze(Object.keys(REVIEW_PACE_SETTINGS));

/**
 * Returns true if the given value is a recognised `reviewPace`.
 *
 * @param {unknown} reviewPace
 * @returns {boolean}
 */
export const isValidReviewPace = (reviewPace) =>
  typeof reviewPace === "string" && REVIEW_PACES.includes(reviewPace);

/**
 * Returns the scheduling settings for the given `reviewPace` value, falling
 * back to standard when unset or unrecognised. Safe against prototype
 * pollution: only own, recognised keys resolve to a settings object.
 *
 * @param {unknown} [reviewPace]
 * @returns {ReviewPaceSettings}
 */
export const reviewPaceSettingsFor = (reviewPace) => {
  if (isValidReviewPace(reviewPace)) {
    return REVIEW_PACE_SETTINGS[
      /** @type {"closer" | "standard" | "wider" | "widest"} */ (reviewPace)
    ];
  }
  return REVIEW_PACE_SETTINGS.standard;
};

/**
 * Cards become due at the start of the user's local day rather than at the
 * exact time their interval expires, so the whole day's workload is visible —
 * and sortable by relevance — from the moment the day begins, instead of
 * trickling in through the day.
 *
 * @param {Date} currentDate
 * @param {number} [timezoneOffset] - minutes relative to UTC, as stored on the
 *   user. Falls back to UTC for users who have never reported one.
 * @returns {Date} the exclusive upper bound on `nextDate` for a due card
 */
export const dueCutoff = (currentDate, timezoneOffset) =>
  dateUtils.addDays(
    dateUtils.snapToLocalDay(currentDate, timezoneOffset ?? 0),
    1
  );

/**
 * @param {string | undefined | null} nextDate - a word's scheduled review date;
 *   unset for words that have never been practiced, which are always due
 * @param {Date} currentDate
 * @param {number} [timezoneOffset] - minutes relative to UTC
 * @returns {boolean}
 */
export const isDue = (nextDate, currentDate, timezoneOffset) =>
  !nextDate ||
  dateUtils.parse(nextDate) < dueCutoff(currentDate, timezoneOffset).getTime();

/**
 * Processes a word recall event, updating the word's spaced repetition data.
 *
 * This function implements a modified SM2 spaced repetition algorithm to update scheduling based on
 * recall quality.
 *
 * @param {Object} userWord - The user's word object to update
 * @param {number} ease - A measure of recall quality, from 0 (complete fail) to 5 (perfect recall)
 *                         < 3: fail
 *                        >= 3: success
 * @param {Date} currentDate - The current date when the recall happened
 * @param {number} [difficulty] - Difficulty of the recall from 0 to 1 (1 = active, 0.5 = passive)
 * @param {Object} [options]
 * @param {boolean} [options.addJitter=true] - Whether to add random variation to the interval
 * @param {number} [options.firstInterval=DAY] - Interval after a perfect recall of a word with
 *                                               no interval yet, driven by the `reviewPace`
 *                                               user preference
 * @param {number} [options.intervalMultiplier=1] - Per-user multiplier on interval growth after
 *                                                  a successful recall, driven by the
 *                                                  `reviewPace` user preference
 * @param {number} [options.timezoneOffset] - minutes relative to UTC, used to find the start of
 *                                            the user's next day
 * @param {RecallSource} [options.source] - Which exercise produced the recall, sent with the
 *                                          save so the server can record it
 */
export const wordRecalled = (
  userWord,
  ease,
  currentDate,
  difficulty,
  {
    addJitter = true,
    firstInterval = DAY,
    intervalMultiplier = 1,
    timezoneOffset,
    source,
  } = {}
) => {
  let actualInterval = 0;

  /** @type {any} */
  const userWordData = _.chain(userWord.viewJSON.value)
    .pick([
      "easinessFactor",
      "recallAttempts",
      "interval",
      "lastRecallEase",
      "nextDate",
    ])
    .defaults({
      easinessFactor: 2.5,
      recallAttempts: 0,
      interval: 0,
      lastRecallEase: 0,
    })
    .value();

  const initialUserWordData = _.clone(userWordData);

  let currentTime = currentDate.getTime();

  if (userWord.hasField("previousIntervalDate")) {
    const previousTime = new Date(
      dateUtils.parse(userWord.state.value.previousIntervalDate)
    ).getTime();

    actualInterval = currentTime - previousTime;
  }

  let difficultyFactor = difficulty;
  if (actualInterval >= 0 && userWordData.interval > 0) {
    difficultyFactor *= Math.min(1, actualInterval / userWordData.interval);
  }

  // This is really just a record of the last time we practiced this word
  userWordData.previousIntervalDate = currentDate.toISOString();

  if (userWordData.recallAttempts > 0) {
    userWordData.previousInterval = userWordData.interval;
  }

  const easinessDelta = 0.1 - (5 - ease) * (0.08 + (5 - ease) * 0.02);
  userWordData.easinessFactor = Math.max(
    1.3,
    // difficulty factor only slows down **increases** in easinessFactor
    userWordData.easinessFactor +
      (easinessDelta > 0 ? easinessDelta * difficultyFactor : easinessDelta)
  );

  if (ease < 3) {
    userWordData.interval = 0;
  } else {
    // Floor on the new interval: half of firstInterval, doubled for a perfect
    // (ease 5) recall. With the default firstInterval of 1 day this matches
    // the original hardcoded 12/24 hour floor.
    const minAmount = (firstInterval / 2) * (1 + Math.max(0, ease - 4));

    userWordData.interval = Math.floor(
      Math.max(
        difficulty * minAmount,
        Math.max(actualInterval, userWordData.interval) *
          (1 +
            intervalMultiplier *
              difficultyFactor *
              (userWordData.easinessFactor - 1))
      )
    );

    if (addJitter) {
      // Add jitter of between -10% and +10% to the interval
      userWordData.interval += Math.floor(
        (Math.random() - 0.5) * 0.2 * userWordData.interval
      );
    }

    // Since a card falls due at the start of the day it's scheduled for (see
    // `dueCutoff`), anything landing later today would come straight back in
    // the same session. Hold successful recalls over until tomorrow at the
    // earliest. Raising the interval rather than pushing `nextDate` out keeps
    // `nextDate - interval` equal to the time of this recall, which is how
    // callers recover when a word was last practiced. Also preserve any later
    // existing due date: even negative jitter must not bring a successful
    // card's next review forward.
    userWordData.interval = Math.max(
      userWordData.interval,
      dueCutoff(currentDate, timezoneOffset).getTime() - currentTime,
      userWordData.nextDate
        ? dateUtils.parse(userWordData.nextDate) - currentTime
        : 0
    );
  }

  userWordData.lastRecallEase = ease;
  userWordData.recallAttempts = userWordData.recallAttempts + 1;

  userWordData.nextDate = new Date(
    currentTime + userWordData.interval
  ).toISOString();

  userWord.save(userWordData, { patch: true, recall: { difficulty, source } });

  if (DEBUG_SRS) {
    console.log(
      `e: ${ease}, d: ${difficulty}, r: ${userWordData.recallAttempts}, easiness: ${initialUserWordData.easinessFactor.toFixed(2)} -> ${userWordData.easinessFactor.toFixed(2)}, actual interval: ${Math.round(actualInterval / dateUtils.DAY)}, interval: ${Math.round(initialUserWordData.interval / dateUtils.DAY)} -> ${Math.round(userWordData.interval / dateUtils.DAY)}`
    );
  }
};

export const getIntervalName = (interval) => {
  if (typeof interval === "undefined") {
    return "notStarted";
  } else if (interval < GETTING_THERE_INTERVAL) {
    return "justStarted";
  } else if (interval < MASTERED_INTERVAL) {
    return "gettingThere";
  } else {
    return "mastered";
  }
};

/**
 * @returns {string[]} the list of names of the groups that `userWord` belongs to
 */
export const calcGroups = (userWord, currentDate, timezoneOffset) => {
  const groupNames = [];

  if (typeof userWord.state.value.nextDate === "undefined") {
    groupNames.push("notStarted");
    groupNames.push("readyToTest");
  } else {
    if (isDue(userWord.state.value.nextDate, currentDate, timezoneOffset)) {
      groupNames.push("readyToTest");
    } else {
      //    groupNames.push('scheduled');
    }
    groupNames.push(getIntervalName(userWord.state.value.interval));
  }

  return groupNames;
};

export const wordsPerDay = (value) => {
  if (typeof value === "undefined") {
    return _wordsPerDay;
  } else {
    _wordsPerDay = value;
  }
};
